import { useCallback, useMemo, useState } from 'react';
import { Button, Search, Tag } from '@carbon/react';
import { Add, ErrorFilled, SubtractAlt, TrashCan } from '@carbon/react/icons';
import type { NavItem } from './types';
import styles from './scenario-nav.module.scss';

interface ScenarioNavProps {
  items: NavItem[];
  selectedId: string | number;
  onSelect: (id: string | number) => void;
  onAdd: () => void;
  onDelete: (id: number) => void;
}

/**
 * Left sidebar navigation for the scenario form.
 * Shows a "Global" button at top, search input, add button,
 * and a scrollable list of area items.
 * @param {ScenarioNavProps} props - The component props
 * @return {JSX.Element} The sidebar navigation component
 */
export function ScenarioNav({
  items,
  selectedId,
  onSelect,
  onAdd,
  onDelete,
}: ScenarioNavProps): JSX.Element {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredItems = useMemo(
    () =>
      items.filter((item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [items, searchTerm]
  );

  /**
   * Handles search input change.
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event
   */
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    []
  );

  /**
   * Handles delete click with stopPropagation.
   * @param {React.MouseEvent} e - The click event
   * @param {number} id - The area ID to delete
   */
  const handleDelete = useCallback(
    (e: React.MouseEvent, id: number) => {
      e.stopPropagation();
      onDelete(id);
    },
    [onDelete]
  );

  return (
    <div className={styles.scenarioNav}>
      <button
        type="button"
        className={`${styles.globalButton} ${
          selectedId === 'global' ? styles.selected : ''
        }`}
        onClick={() => onSelect('global')}
      >
        Global
      </button>
      <div className={styles.searchRow}>
        <Search
          size="lg"
          placeholder="Search list below"
          labelText="Search areas"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Button
          kind="primary"
          hasIconOnly
          renderIcon={Add}
          iconDescription="Add area"
          tooltipPosition="bottom"
          className={styles.addButton}
          onClick={onAdd}
        />
      </div>
      <div className={styles.areaList}>
        {filteredItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`${styles.areaItem} ${
              selectedId === item.id ? styles.selected : ''
            }`}
            onClick={() => onSelect(item.id)}
          >
            <span className={styles.areaName}>{item.name}</span>
            {(item.hasOverlap || item.hasInvalidParams) && (
              // One icon for either cause — two stacked red glyphs on a single
              // row is noise. The name distinguishes them, and replaces the
              // previous icon-only indicator that had no accessible name.
              <ErrorFilled
                className={styles.overlapIcon}
                aria-label={[
                  item.hasOverlap ? 'Overlaps with another area' : null,
                  item.hasInvalidParams ? 'Has invalid parameters' : null,
                ]
                  .filter(Boolean)
                  .join('; ')}
              />
            )}
            {item.status === 'new' && (
              <Tag type="blue" size="sm" className={styles.newTag}>
                New
              </Tag>
            )}
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={item.status === 'new' ? SubtractAlt : TrashCan}
              iconDescription="Delete area"
              tooltipPosition="left"
              className={styles.deleteButton}
              onClick={(e: React.MouseEvent) => handleDelete(e, item.id)}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
